import { Group, Loader, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminRO } from '@routellers/types/dist/admin/admin.ro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';

type StatsRow = {
    date: string;
    workingHours: number;
    workingMinutes: number;
    totalRequests: number;
    photosAdded: number;
    textsChanged: number;
    statusesChanged: number;
};

const formatDateInput = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const daysAgo = (n: number) => {
    const date = new Date();
    date.setDate(date.getDate() - n);
    return date;
};

export const UsersStatistics = () => {
    const { t } = useTranslation();
    const [admins, setAdmins] = useState<AdminRO[]>([]);
    const [adminsLoading, setAdminsLoading] = useState(true);
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
    const [startDate, setStartDate] = useState(() => formatDateInput(daysAgo(7)));
    const [endDate, setEndDate] = useState(() => formatDateInput(new Date()));
    const [rows, setRows] = useState<StatsRow[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setAdminsLoading(true);
            try {
                const { data } = await api.admins.query(undefined);
                if (!cancelled) setAdmins(data || []);
            } catch (error) {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                    setAdmins([]);
                }
            } finally {
                if (!cancelled) setAdminsLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const loadStats = useCallback(async () => {
        if (!startDate || !endDate) {
            setRows([]);
            return;
        }

        setLoading(true);
        try {
            const { data } = await api.admins.getAllAdminsActivityStats({
                adminEmail: selectedEmail || 'all',
                startDate,
                endDate,
            });
            setRows(
                (data || []).map((item) => ({
                    date: item.date,
                    workingHours: item.workingHours,
                    workingMinutes: item.workingMinutes,
                    totalRequests: item.totalRequests,
                    photosAdded: item.photosAdded,
                    textsChanged: item.textsChanged,
                    statusesChanged: item.statusesChanged,
                })),
            );
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [selectedEmail, startDate, endDate]);

    useEffect(() => {
        void loadStats();
    }, [loadStats]);

    const adminOptions = useMemo(
        () => [
            { value: 'all', label: t('pages.users.statistics.allAdmins') },
            ...admins.map((admin) => ({
                value: admin.email,
                label: `${admin.name} (${admin.email})`,
            })),
        ],
        [admins, t],
    );

    if (adminsLoading) return <Loader />;

    return (
        <Stack gap='md'>
            <Group gap='md' align='flex-end' wrap='wrap'>
                <Select
                    label={t('pages.users.statistics.admin')}
                    data={adminOptions}
                    value={selectedEmail ?? 'all'}
                    onChange={(value) =>
                        setSelectedEmail(!value || value === 'all' ? null : value)
                    }
                    searchable
                    w={320}
                />
                <TextInput
                    type='date'
                    label={t('pages.users.statistics.startDate')}
                    value={startDate}
                    onChange={(e) => setStartDate(e.currentTarget.value)}
                    w={180}
                />
                <TextInput
                    type='date'
                    label={t('pages.users.statistics.endDate')}
                    value={endDate}
                    onChange={(e) => setEndDate(e.currentTarget.value)}
                    w={180}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <Table.ScrollContainer minWidth={860}>
                    <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('pages.users.statistics.columns.date')}</Table.Th>
                                <Table.Th>
                                    {t('pages.users.statistics.columns.workingHours')}
                                </Table.Th>
                                <Table.Th>
                                    {t('pages.users.statistics.columns.workingMinutes')}
                                </Table.Th>
                                <Table.Th>
                                    {t('pages.users.statistics.columns.photosAdded')}
                                </Table.Th>
                                <Table.Th>
                                    {t('pages.users.statistics.columns.textsChanged')}
                                </Table.Th>
                                <Table.Th>
                                    {t('pages.users.statistics.columns.statusesChanged')}
                                </Table.Th>
                                <Table.Th>
                                    {t('pages.users.statistics.columns.totalRequests')}
                                </Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((row) => (
                                <Table.Tr key={row.date}>
                                    <Table.Td>{row.date}</Table.Td>
                                    <Table.Td>{row.workingHours}</Table.Td>
                                    <Table.Td>{row.workingMinutes}</Table.Td>
                                    <Table.Td>{row.photosAdded}</Table.Td>
                                    <Table.Td>{row.textsChanged}</Table.Td>
                                    <Table.Td>{row.statusesChanged}</Table.Td>
                                    <Table.Td>{row.totalRequests}</Table.Td>
                                </Table.Tr>
                            ))}
                            {!rows.length && (
                                <Table.Tr>
                                    <Table.Td colSpan={7}>
                                        <Text size='sm' c='dimmed'>
                                            {t('pages.users.statistics.empty')}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            )}
        </Stack>
    );
};
